import { useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, ArrowLeft, Calendar, Clock } from "lucide-react";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import { POSTS, CATEGORIES } from "@/data/blog";
import { Badge } from "@/components/ui/badge";

export function BlogList() {
  const [active, setActive] = useState("Tous");
  const filtered = useMemo(
    () => (active === "Tous" ? POSTS : POSTS.filter((p) => p.category === active)),
    [active]
  );

  return (
    <div data-testid="page-blog" className="page-enter pt-32">
      <SEO
        title="Blog — Psychologue Paris 16e | Anxiété, trauma, burn-out"
        description="Articles de fond sur l'anxiété, le burn-out, le psychotraumatisme, le sommeil, la psychosomatique et les relations."
      />
      <section className="container-fxv py-16">
        <Reveal>
          <p className="overline gold-line">Journal</p>
        </Reveal>
        <Reveal delay={0.05}>
          <h1 className="font-serif text-5xl md:text-6xl text-[#0C2340] mt-6 max-w-3xl leading-[1.05]">
            Repères et éclairages pour un cheminement intérieur
          </h1>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="text-[#4A5568] mt-6 max-w-2xl text-lg leading-relaxed">
            Articles de fond, rédigés avec rigueur, pour comprendre les
            mécanismes psychologiques et apprivoiser les difficultés du
            quotidien.
          </p>
        </Reveal>
      </section>

      <section className="container-fxv pb-8 flex flex-wrap gap-2">
        {["Tous", ...CATEGORIES].map((c) => (
          <button
            key={c}
            data-testid={`blog-cat-${c}`}
            onClick={() => setActive(c)}
            className={`px-5 py-2 text-sm rounded-full border transition-colors ${
              active === c
                ? "bg-[#0C2340] text-white border-[#0C2340]"
                : "bg-white text-[#0C2340] border-[#E2E8F0] hover:border-[#0C2340]"
            }`}
          >
            {c}
          </button>
        ))}
      </section>

      <section className="container-fxv pb-24 grid md:grid-cols-2 lg:grid-cols-3 gap-8">
        {filtered.map((p, i) => (
          <Reveal key={p.slug} delay={i * 0.05}>
            <Link
              to={`/blog/${p.slug}`}
              data-testid={`blog-card-${p.slug}`}
              className="group block"
            >
              <div className="aspect-[4/3] overflow-hidden bg-[#F0F4F8]">
                <img
                  src={p.cover}
                  alt={p.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="mt-6">
                <Badge className="bg-[#0C2340] text-white hover:bg-[#163861] rounded-none">
                  {p.category}
                </Badge>
                <h2 className="font-serif text-2xl text-[#0C2340] mt-4 leading-snug group-hover:text-[#C5A059] transition-colors">
                  {p.title}
                </h2>
                <p className="text-[#4A5568] text-sm mt-3 leading-relaxed line-clamp-3">
                  {p.excerpt}
                </p>
                <div className="mt-4 flex items-center gap-4 text-xs text-[#4A5568]">
                  <span className="inline-flex items-center gap-1">
                    <Calendar size={12} strokeWidth={1.5} /> {p.date}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Clock size={12} strokeWidth={1.5} /> {p.readingTime}
                  </span>
                </div>
              </div>
            </Link>
          </Reveal>
        ))}
      </section>
    </div>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const post = POSTS.find((p) => p.slug === slug);

  if (!post) {
    return (
      <div className="pt-40 container-fxv text-center" data-testid="post-not-found">
        <p className="font-serif text-3xl text-[#0C2340]">Article introuvable</p>
        <Link to="/blog" className="text-[#C5A059] mt-4 inline-block">
          Retour au blog
        </Link>
      </div>
    );
  }

  const related = POSTS.filter((p) => p.slug !== slug).slice(0, 3);

  return (
    <article
      data-testid={`page-blog-post-${post.slug}`}
      className="page-enter pt-32"
    >
      <SEO
        title={`${post.title} — Blog FXV Psy`}
        description={post.excerpt}
      />
      <section className="container-fxv py-10">
        <Link
          to="/blog"
          className="text-sm text-[#4A5568] hover:text-[#0C2340] inline-flex items-center gap-2"
        >
          <ArrowLeft size={16} strokeWidth={1.5} /> Tous les articles
        </Link>
      </section>
      <header className="container-fxv max-w-3xl pb-10">
        <Badge className="bg-[#C5A059] text-[#0C2340] hover:bg-[#D4B374] rounded-none">
          {post.category}
        </Badge>
        <h1 className="font-serif text-4xl md:text-6xl text-[#0C2340] mt-6 leading-[1.05]">
          {post.title}
        </h1>
        <div className="mt-6 flex items-center gap-6 text-sm text-[#4A5568]">
          <span className="inline-flex items-center gap-2">
            <Calendar size={14} strokeWidth={1.5} /> {post.date}
          </span>
          <span className="inline-flex items-center gap-2">
            <Clock size={14} strokeWidth={1.5} /> {post.readingTime}
          </span>
        </div>
      </header>
      <div className="container-fxv max-w-5xl">
        <img
          src={post.cover}
          alt={post.title}
          className="w-full aspect-[16/9] object-cover"
        />
      </div>
      <section className="container-fxv max-w-3xl py-16">
        {post.content.map((para, i) => (
          <p
            key={`${post.slug}-p-${i}`}
            className="text-lg leading-relaxed text-[#1A1A1A] mt-6 first:mt-0 font-light"
          >
            {para}
          </p>
        ))}
      </section>

      <section className="bg-[#F0F4F8] section-fxv">
        <div className="container-fxv">
          <p className="overline gold-line">À lire aussi</p>
          <div className="mt-8 grid md:grid-cols-3 gap-8">
            {related.map((r) => (
              <Link
                key={r.slug}
                to={`/blog/${r.slug}`}
                className="group block bg-white"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={r.cover}
                    alt={r.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="p-6">
                  <p className="text-xs uppercase tracking-[0.18em] text-[#C5A059]">
                    {r.category}
                  </p>
                  <p className="font-serif text-xl text-[#0C2340] mt-3 group-hover:text-[#C5A059] transition-colors">
                    {r.title}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="section-fxv bg-white">
        <div className="container-fxv text-center max-w-2xl">
          <p className="overline gold-line">Prendre rendez-vous</p>
          <h2 className="font-serif text-4xl md:text-5xl text-[#0C2340] mt-6">
            Échangeons en consultation.
          </h2>
          <p className="text-[#4A5568] mt-6 leading-relaxed">
            Si cet article résonne avec ce que vous traversez, n'hésitez pas à
            me contacter. Une première séance permet de poser les bases d'un
            travail adapté.
          </p>
          <Link
            to="/contact"
            data-testid="blog-cta"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#0C2340] text-white px-7 py-3.5 text-sm font-medium hover:bg-[#163861]"
          >
            Prendre rendez-vous <ArrowRight size={16} strokeWidth={1.5} />
          </Link>
        </div>
      </section>
    </article>
  );
}
